import React, { useEffect, useRef, useState } from 'react';
import { Link } from "react-router-dom";
import Fuse from 'fuse.js';

import Button from '../../Button';
import SearchAddButton from '../../SearchAddButton';
import Dropdown from '../../Dropdown';
import EditTeacherData from '../modals/edit_teacher';

import { formatTime, sortArrayOfObjects } from '../../../js/Helpers';
import { saveDataToDatabase } from '../../../js/SaveFunctions';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlus, faLock, faCheckSquare, faSquare, faTimes, faSearch, faCalendarDays, faCheckCircle, faChevronLeft, faChevronRight, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

import '../../../css/elementary/auto_scheduling_setup.css';

export default function SpecialsCourses({ isLoading, coursesLoading = false, subscreenLocked, subscreenRestricted, schoolInfo, scheduleInfo, periods, courses, setCourses, teachers, setTeachers, students, setStudents, classrooms, homeroomGradeGroups = [], handleMoveToNextScreen }) {
	const [selectedCourseID, setSelectedCourseID] = useState(null);
	const [openSearches, setOpenSearches] = useState({courses:false, teachers:false, classrooms:false, homeroom_teachers: false})
	const [searchResults, setSearchResults] = useState({courses:[], teachers:[], classrooms:[], homeroom_teachers:[]});
	const [days, setDays] = useState([]);
	const [showCourseStatusHelp, setShowCourseStatusHelp] = useState(false);
	const courseStatusHelpRef = useRef(null);
	const teachersStepRef = useRef(null);
	const homeroomsStepRef = useRef(null);
	const periodsStepRef = useRef(null);
	const coursesRef = useRef(courses);
	const courseSaveQueueRef = useRef({inFlight:false, pendingRequest:null});


	const [showEditDataModal, setShowEditDataModal] = useState(false);
	const [currentDataID, setCurrentDataID] = useState(null);
	const selectedCourseStorageKey = `elem_specials_course_selected_${schoolInfo?.school_id || 'unknown-school'}_${schoolInfo?.current_schedule_version_id || 'unknown-schedule'}`;

	const group_positions = {
		'1':{'0':{start:'1', end:'120'}}, 
		'2':{'0':{start:'1', end:'58'},'1':{start:'62', end:'120'}}, 
		'3':{'0':{start:'1', end:'38'},'1':{start:'41', end:'78'},'2':{start:'82', end:'120'}}, 
		'4':{'0':{start:'1', end:'27'},'1':{start:'32', end:'58'},'2':{start:'63', end:'89'},'3':{start:'94', end:'120'}}
	};

	const normalizeGradeLabel = (grade_value) => {
		if(grade_value === null || typeof grade_value === 'undefined') return '';
		const grade_string = `${grade_value}`.trim();
		if(grade_string === '0') return 'K';
		if(grade_string.toLowerCase() === 'k') return 'K';
		return grade_string;
	};

	const formatGradeDisplay = (grade_label) => {
		const normalized_label = normalizeGradeLabel(grade_label);
		if(!normalized_label) return '';
		if(normalized_label.toUpperCase() === 'PK') return 'PreK';
		if(normalized_label.toUpperCase() === 'K') return 'Kindergarten';
		return `${normalized_label}`;
	};

	const sortGradeLabels = (grade_label_a, grade_label_b) => {
		const label_a = normalizeGradeLabel(grade_label_a);
		const label_b = normalizeGradeLabel(grade_label_b);

		if(label_a === 'PK') return -1;
		if(label_b === 'PK') return 1;
		if(label_a === 'K') return -1;
		if(label_b === 'K') return 1;

		const label_a_number = parseInt(label_a, 10);
		const label_b_number = parseInt(label_b, 10);
		const label_a_is_number = !Number.isNaN(label_a_number);
		const label_b_is_number = !Number.isNaN(label_b_number);

		if(label_a_is_number && label_b_is_number) return label_a_number - label_b_number;
		return label_a.localeCompare(label_b);
	};

	const getDayPeriodKey = (day_number, period_id) => {
		const normalized_day_number = `${day_number || ''}`.trim();
		const normalized_period_id = `${period_id || ''}`.trim();
		if(!normalized_day_number || !normalized_period_id) return '';
		return `${normalized_day_number}__${normalized_period_id}`;
	};

	const getUniqueDayPeriodRestrictions = (day_period_restrictions = []) => {
		const unique_restrictions = [];
		const logged_keys = new Set();

		(Array.isArray(day_period_restrictions) ? day_period_restrictions : []).forEach((restriction) => {
			const key = getDayPeriodKey(restriction?.day_number, restriction?.period_id);
			if(!key || logged_keys.has(key)) return;
			logged_keys.add(key);

			const [day_number, period_id] = key.split('__');
			unique_restrictions.push({day_number, period_id});
		});

		return unique_restrictions;
	};

	const getCourseRestrictedDayPeriodKeySet = (course_info) => {
		const restrictions = Array.isArray(course_info?.day_period_restrictions) ? course_info.day_period_restrictions : [];
		const key_set = new Set();

		restrictions.forEach((restriction) => {
			const key = getDayPeriodKey(restriction?.day_number, restriction?.period_id);
			if(!key) return;
			key_set.add(key);
		});

		return key_set;
	};

	const getHomeroomGradeResults = () => {
		const teacher_id_map = teachers.reduce((results, teacher) => {
			results[`${teacher.teacher_id}`] = teacher.teacher_id;
			return results;
		}, {});

		return (homeroomGradeGroups || []).reduce((results, grade_group) => {
			const grade_label = normalizeGradeLabel(grade_group.label);
			if(!grade_label) return results;

			const unique_teacher_ids = (Array.isArray(grade_group.teacher_ids) ? grade_group.teacher_ids : []).reduce((teacher_results, teacher_id) => {
				const normalized_teacher_id = `${teacher_id}`;
				if(!(normalized_teacher_id in teacher_id_map)) return teacher_results;
				const teacher_id_value = teacher_id_map[normalized_teacher_id];
				if(teacher_results.includes(teacher_id_value)) return teacher_results;
				teacher_results.push(teacher_id_value);
				return teacher_results;
			}, []);

			if(unique_teacher_ids.length === 0) return results;

			results.push({
				result_type: 'grade',
				grade_label,
				display_name: formatGradeDisplay(grade_label),
				teacher_ids: unique_teacher_ids
			});

			return results;
		}, []).sort((result_a, result_b) => sortGradeLabels(result_a.grade_label, result_b.grade_label));
	};
	const getCourseSearchResults = () => {
		return courses
			.sort((course_a, course_b) => `${course_a.name || ''}`.toLowerCase().localeCompare(`${course_b.name || ''}`.toLowerCase()));
	};
	const courseSaveErrorMessage = 'We had an issue saving your changes. Please try again.';

	useEffect(() => {
		coursesRef.current = courses;
	}, [courses]);
	
	/////////////////////
	///// FUNCTIONS /////
	/////////////////////
	const rollbackCourseSave = (course_index, previous_course_info, previous_open_searches = null) => {
		const reverted_courses = [...coursesRef.current];
		reverted_courses[course_index] = previous_course_info;
		setCourses(reverted_courses);
		if(previous_open_searches !== null) setOpenSearches(previous_open_searches);
	};

	const flushQueuedCourseSave = async () => {
		if(courseSaveQueueRef.current.inFlight) return null;
		const next_request = courseSaveQueueRef.current.pendingRequest;
		if(!next_request) return null;

		courseSaveQueueRef.current.inFlight = true;
		courseSaveQueueRef.current.pendingRequest = null;

		try {
			const response_data = await saveDataToDatabase({
				school_id:schoolInfo.school_id,
				schedule_version_id:schoolInfo.current_schedule_version_id,
				data_type:'course',
				course_data:next_request.course_data,
				database:'elementary'
			});
			if(!response_data) throw new Error('save_failed');
			return response_data;
		} catch(error) {
			console.log(error?.response || error);
			courseSaveQueueRef.current.pendingRequest = null;
			rollbackCourseSave(next_request.course_index, next_request.previous_course_info, next_request.previous_open_searches);
			alert(courseSaveErrorMessage);
			return null;
		} finally {
			courseSaveQueueRef.current.inFlight = false;
			if(courseSaveQueueRef.current.pendingRequest) flushQueuedCourseSave();
		}
	};

	const saveCourseChanges = async (course_index, previous_course_info, previous_open_searches = null) => {
		const latest_course_info = coursesRef.current[course_index];
		if(!latest_course_info) return null;

		courseSaveQueueRef.current.pendingRequest = {
			course_index,
			course_data: JSON.parse(JSON.stringify(latest_course_info)),
			previous_course_info,
			previous_open_searches
		};

		return flushQueuedCourseSave();
	};

	const updateCourse = async (course_id, data_index, new_value, change_type = 'change') => {
		const course_index = courses.findIndex(course => course.course_id === course_id);

		if(course_index === -1) return;
		const previous_course_info = JSON.parse(JSON.stringify(courses[course_index]));
		const previous_open_searches = {...openSearches};

		if(data_index === 'teachers_can_teach')
		{
			const tct_index = courses[course_index]['teachers_can_teach'].findIndex(tct => tct.teacher_id === new_value);
			if(change_type ==='add' && tct_index === -1) 
			{
				courses[course_index]['teachers_can_teach'].push({teacher_id:new_value, type:'1'});
			}
			else if(change_type ==='remove' && tct_index !== -1) 
			{
				courses[course_index]['teachers_can_teach'].splice(tct_index, 1);
			}

			if(change_type !== 'add')
			{
				openSearches['teachers'] = false;
			}
		}
		else if(data_index === 'classroom_restrictions')
		{
			const cr_index = courses[course_index]['classroom_restrictions'].findIndex(classroom_id => classroom_id === new_value);
			if(change_type === 'add' && cr_index === -1) 
			{
				courses[course_index]['classroom_restrictions'].push(new_value);
			}
			else if(change_type ==='remove' && cr_index !== -1) 
			{
				courses[course_index]['classroom_restrictions'].splice(cr_index, 1);
			}

			if(change_type !== 'add')
			{
				openSearches['classrooms'] = false;
			}
		}
		else if(data_index === 'course_homerooms')
		{
			const hwns_index = courses[course_index]['course_homerooms'].findIndex(teacher_id => teacher_id === new_value);
			if(change_type === 'add' && hwns_index === -1) 
			{
				courses[course_index]['course_homerooms'].push(new_value);
			}
			else if(change_type ==='remove' && hwns_index !== -1) 
			{
				courses[course_index]['course_homerooms'].splice(hwns_index, 1);
			}

			if(change_type !== 'add')
			{
				openSearches['homeroom_teachers'] = false;
			}
		}
		else if(data_index === 'day_period_restrictions')
		{
			const normalized_restrictions = getUniqueDayPeriodRestrictions(courses[course_index]['day_period_restrictions']);
			courses[course_index]['day_period_restrictions'] = normalized_restrictions;

			const key_to_update = getDayPeriodKey(new_value?.day_number, new_value?.period_id);
			if(!key_to_update) return;

			const dpr_index = courses[course_index]['day_period_restrictions'].findIndex((day_period_restriction) => {
				return getDayPeriodKey(day_period_restriction?.day_number, day_period_restriction?.period_id) === key_to_update;
			});

			if(change_type === 'add' && dpr_index === -1) 
			{
				const [normalized_day_number, normalized_period_id] = key_to_update.split('__');
				courses[course_index]['day_period_restrictions'].push({day_number:normalized_day_number, period_id:normalized_period_id});
			}
			else if(change_type ==='remove' && dpr_index !== -1) 
			{
				courses[course_index]['day_period_restrictions'].splice(dpr_index, 1);
			}
		}
		else 
		{
			courses[course_index][data_index] = new_value;
			if(data_index === 'is_special' && `${new_value}` !== '1')
			{
				openSearches['courses'] = false;
			}
		}

		setCourses([...courses]);
		setSearchResults({...searchResults});
		setOpenSearches({...openSearches});
		
		await saveCourseChanges(course_index, previous_course_info, previous_open_searches);
	}

	const toggleDayPeriodSelection = (day_number, period_id, is_restricted) => {
		if(!selectedCourseID) return;
		const update_payload = {day_number: `${day_number}`, period_id: `${period_id}`};
		const change_type = (is_restricted) ? 'remove' : 'add';
		updateCourse(selectedCourseID, 'day_period_restrictions', update_payload, change_type);
	};

	const calculatePeriodColumnStartEnd = (period_data, day_schedule, logged_positions, start_time, end_time) => {
		const overlapCheck = (time1, time2) => {
			const start_time1 = time1.start_time;
			const end_time1 = time1.end_time;
			const start_time1_array = start_time1.split(':');
			const end_time1_array = end_time1.split(':');

			const start_time1_number = parseInt(start_time1_array[0] + start_time1_array[1]);
			const end_time1_number = parseInt(end_time1_array[0] + end_time1_array[1]);

			const start_time2 = time2.start_time;
			const end_time2 = time2.end_time;
			const start_time2_array = start_time2.split(':');
			const end_time2_array = end_time2.split(':');

			const start_time2_number = parseInt(start_time2_array[0] + start_time2_array[1]);
			const end_time2_number = parseInt(end_time2_array[0] + end_time2_array[1]);

			if(end_time1_number > start_time2_number && end_time2_number > start_time1_number) return true;

			return false;
		}

		const period_id = period_data.period_id;

		///// Calculate left and right position
		// Get max number of overlaps
		const period_time = {period_id:period_id, start_time:start_time, end_time:end_time};

		let overlapping_time_groups = [[period_time]];
		for(var i = 0; i < day_schedule.length; i++)
		{
			const new_period = day_schedule[i];
			const new_period_id = new_period.period_id;

			if(period_id === new_period_id) continue; //ignore the original period itself

			const new_time = {period_id:new_period_id, start_time:new_period.start_time, end_time:new_period.end_time};
			const new_time_overlaps_original = overlapCheck(period_time, new_time);

			if(!new_time_overlaps_original) continue; //ignore periods that don't even overlap original

			// Go through all overlap groups and see if new period overlaps all of the times in the current groups
			// If yes, add to that group
			// If no, create a new group
			for(var j = 0; j < overlapping_time_groups.length; j++)
			{
				const overlapping_time_group = overlapping_time_groups[j];

				// Ignore a period that has already been added to this group
				const new_time_index = overlapping_time_group.findIndex(overlapping_time => overlapping_time.period_id === new_period_id);
				if(new_time_index !== -1) continue;

				let new_time_overlaps_entire_group = true;
				
				for(var k = 0; k < overlapping_time_group.length; k++)
				{
					const time_to_check = overlapping_time_group[k];
					const time_overlaps = overlapCheck(time_to_check, new_time);

					if(!time_overlaps) new_time_overlaps_entire_group = false;
				}

				if(new_time_overlaps_entire_group)
				{
					overlapping_time_groups[j].push(new_time);
				}
				else
				{
					overlapping_time_groups.push([period_time, new_time]);
				}
			}
		}

		const overall_max_stats = overlapping_time_groups.reduce((results, overlapping_time_group) => {
			const overlapping_time_group_length = overlapping_time_group.length;
			if(overlapping_time_group_length > results.max_overlaps) results = {max_overlaps: overlapping_time_group_length, overlapping_periods:overlapping_time_group};
			return results;
		}, {max_overlaps:0, overlapping_periods:[]});

		/// Use max overlaps to get the possible positions (preset depending on number of overlaps)
		const max_overlaps = overall_max_stats.max_overlaps;
		const overlapping_periods = overall_max_stats.overlapping_periods;

		const possible_positions = group_positions[max_overlaps];
		const possible_positions_indexes = Object.keys(possible_positions);
		const used_positions = overlapping_periods.reduce((results, period_times) => {
			const overlapping_period_id = period_times.period_id;
			const used_position = (overlapping_period_id in logged_positions) ? logged_positions[overlapping_period_id] : null;
			if(used_position) results.push(used_position);
			return results;
		},[]);
		const unused_positions = possible_positions_indexes.filter(x => used_positions.indexOf(x) === -1);
		const position_index = unused_positions[0];
		const start_position = possible_positions[position_index].start;
		const end_position = possible_positions[position_index].end;

		return {position_index:position_index, col_start:start_position, col_end:end_position};
	}

	const toggleAddNewConnection = (searchType) => {
		const new_value = !openSearches[searchType];

		if(new_value)
		{
			if(searchType === 'courses')
			{
				searchResults[searchType] = getCourseSearchResults();
			}
			else if(searchType === 'teachers')
			{
				searchResults[searchType] = teachers;
			}
			else if(searchType === 'homeroom_teachers')
			{
				searchResults[searchType] = [...getHomeroomGradeResults(), ...teachers];
			}
			else
			{
				searchResults[searchType] = classrooms;
			}
		}

		openSearches[searchType] = new_value;

		setSearchResults({...searchResults});
		setOpenSearches({...openSearches});
	}
	
	const toggleEditData = (dataID) => {
		if(showEditDataModal)
		{
			setShowEditDataModal(false);
			return;
		}

		setCurrentDataID(dataID);
		setShowEditDataModal(true);
	}

	const fuzzySearch = (e, dataType, dataKey = dataType) => {
		const search_value = e.target.value;
		
		if(dataType === 'courses')
		{
			const all_courses = getCourseSearchResults();
			const fuse = new Fuse(all_courses, {
				keys: ['name', 'course_code'],
				threshold: .1
			})
			
			const results = fuse.search(search_value);
			const course_results = (search_value.length > 0) ? results.map(result => result.item).slice(0, 6) : all_courses;
			
			searchResults[dataKey] = course_results;
		}
		else if(dataType === 'teachers')
		{
			const fuse = new Fuse(teachers, {
				keys: ['first_name', 'name'],
				threshold: .1
			})
			
			const results = fuse.search(search_value);
			const teacher_results = (search_value.length > 0) ? results.map(result => result.item).slice(0, 6) : teachers;
			
			searchResults[dataKey] = teacher_results;
		}
		else if(dataType === 'classrooms')
		{
			const fuse = new Fuse(classrooms, {
				keys: ['name'],
				threshold: .1
			})
			
			const results = fuse.search(search_value);
			const classroom_results = (search_value.length > 0) ? results.map(result => result.item).slice(0, 6) : classrooms;
			
			searchResults[dataKey] = classroom_results;
		}
		else if(dataType === 'homeroom_teachers')
		{
			const grade_results = getHomeroomGradeResults();
			const filtered_grade_results = (search_value.length > 0) ? (() => {
				const fuse = new Fuse(grade_results, {
					keys: ['display_name', 'grade_label'],
					threshold: .1
				});

				return fuse.search(search_value).map(result => result.item).slice(0, 6);
			})() : grade_results;

			const fuse = new Fuse(teachers, {
				keys: ['first_name', 'name'],
				threshold: .1
			})
			
			const results = fuse.search(search_value);
			const teacher_results = (search_value.length > 0) ? results.map(result => result.item).slice(0, 6) : teachers;
			
			searchResults[dataKey] = [...filtered_grade_results, ...teacher_results];
		}

		setSearchResults({...searchResults});
	}

	const addHomeroomTeachersByGrade = async (course_id, grade_teacher_ids) => {
		const course_index = courses.findIndex(course => course.course_id === course_id);
		if(course_index === -1) return;
		const previous_course_info = JSON.parse(JSON.stringify(courses[course_index]));
		const previous_open_searches = {...openSearches};

		const existing_teacher_ids = courses[course_index]['course_homerooms'];
		let changed = false;
		grade_teacher_ids.forEach((teacher_id) => {
			if(existing_teacher_ids.includes(teacher_id)) return;
			courses[course_index]['course_homerooms'].push(teacher_id);
			changed = true;
		});
		if(!changed) return;

		setCourses([...courses]);
		setSearchResults({...searchResults});
		setOpenSearches({...openSearches});
		await saveCourseChanges(course_index, previous_course_info, previous_open_searches);
	}

	////////////////////////////////
	///// USE EFFECT /////
	////////////////////////////////
	// If selected special is removed or no longer a special, go back to list view
	useEffect(() => {
		if(!selectedCourseID) return;

		const selectedCourseStillExists = courses.some(course => course.course_id === selectedCourseID && course.is_special === '1');
		if(!selectedCourseStillExists) setSelectedCourseID(null);
	}, [courses, selectedCourseID]);

	// Restore selected special course after refresh
	useEffect(() => {
		if(selectedCourseID) return;
		const stored_course_id = sessionStorage.getItem(selectedCourseStorageKey);
		if(!stored_course_id) return;

		const selectedCourseStillExists = courses.some(course => `${course.course_id}` === `${stored_course_id}` && course.is_special === '1');
		if(selectedCourseStillExists) setSelectedCourseID(stored_course_id);
	}, [courses, selectedCourseID, selectedCourseStorageKey]);

	// Persist selected special course
	useEffect(() => {
		if(selectedCourseID) sessionStorage.setItem(selectedCourseStorageKey, selectedCourseID);
	}, [selectedCourseID, selectedCourseStorageKey]);

	// Clear selected special course when leaving this screen
	useEffect(() => {
		return () => {
			sessionStorage.removeItem(selectedCourseStorageKey);
		};
	}, [selectedCourseStorageKey]);

	useEffect(() => {
		const num_days_in_cycle = scheduleInfo.num_days_in_cycle;

		let new_days = [];

		for(let day = 1; day <= num_days_in_cycle; day++)
		{
			new_days.push(`${day}`);
		}

		setDays(new_days);
	}, [scheduleInfo]);

	useEffect(() => {
		setShowCourseStatusHelp(false);
	}, [selectedCourseID]);

	useEffect(() => {
		if(!showCourseStatusHelp) return;

		const handleOutsideClick = (event) => {
			if(!courseStatusHelpRef.current) return;
			if(!courseStatusHelpRef.current.contains(event.target)) setShowCourseStatusHelp(false);
		};

		document.addEventListener('mousedown', handleOutsideClick);
		return () => {
			document.removeEventListener('mousedown', handleOutsideClick);
		};
	}, [showCourseStatusHelp]);

	useEffect(() => {
		const hasOpenSearch = Object.values(openSearches).some(Boolean);
		if(!hasOpenSearch) return;

		const handleOutsideClick = (event) => {
			const clickedInsideSearch = event.target.closest('.search-add-row-container');
			if(clickedInsideSearch) return;
			setOpenSearches({courses:false, teachers:false, classrooms:false, homeroom_teachers:false});
		};

		document.addEventListener('mousedown', handleOutsideClick);
		return () => {
			document.removeEventListener('mousedown', handleOutsideClick);
		};
	}, [openSearches]);

	////////////////////////////////
	///// INITIALIZE VARIABLES /////
	////////////////////////////////
	const numTimesNeededOptions = [
		{value:null, display: 'No Limit'},
		{value:'1', display: '1'},
		{value:'2', display: '2'},
		{value:'3', display: '3'},
		{value:'4', display: '4'},
		{value:'5', display: '5'},
		{value:'6', display: '6'},
		{value:'7', display: '7'},
		{value:'8', display: '8'},
		{value:'9', display: '9'},
		{value:'10', display: '10'},
		{value:'11', display: '11'},
		{value:'12', display: '12'},
	];

	const courseInfo = (selectedCourseID) ? courses.find(course => course.course_id === selectedCourseID) : null;
	const specialName = (courseInfo) ? courseInfo.name : null;
	const numTimesNeeded = (courseInfo) ? courseInfo.num_times_needed : null;
	const classroomRestrictions = (courseInfo) ? courseInfo.classroom_restrictions : [];
	const teachersCanTeach = (courseInfo) ? courseInfo.teachers_can_teach : [];
	const courseHomerooms = (courseInfo) ? courseInfo.course_homerooms : [];
	const totalSpecialDayPeriodKeySet = periods.reduce((results, period) => {
		if(`${period?.is_special || '0'}` !== '1') return results;
		const period_id = `${period?.period_id || ''}`.trim();
		if(!period_id) return results;

		const period_days = Array.isArray(period?.days) ? period.days : [];
		period_days.forEach((day_number) => {
			const key = getDayPeriodKey(day_number, period_id);
			if(key) results.add(key);
		});

		return results;
	}, new Set());
	const totalSpecialDayPeriodCount = totalSpecialDayPeriodKeySet.size;
	const selectedCourseRestrictedDayPeriodKeySet = getCourseRestrictedDayPeriodKeySet(courseInfo);
	const selectedCourseRestrictedValidCount = [...selectedCourseRestrictedDayPeriodKeySet].filter((key) => totalSpecialDayPeriodKeySet.has(key)).length;
	const selectableDayPeriodsCount = Math.max(0, totalSpecialDayPeriodCount - selectedCourseRestrictedValidCount);
	const hasTeachersCanTeach = teachersCanTeach.length > 0;
	const hasCourseHomerooms = courseHomerooms.length > 0;
	const hasDayPeriodRestrictions = selectableDayPeriodsCount > 0;
	const selectedCourseComplete = hasTeachersCanTeach && hasCourseHomerooms && hasDayPeriodRestrictions;
	const selectedCourseCompletedCount = [hasTeachersCanTeach, hasCourseHomerooms, hasDayPeriodRestrictions].filter(Boolean).length;
	const courseCompletionRequirements = [
		{key:'teachers', label:'Add at least one teacher that can teach this course', complete: hasTeachersCanTeach, ref: teachersStepRef},
		{key:'homerooms', label:'Add at least one homeroom that needs this course', complete: hasCourseHomerooms, ref: homeroomsStepRef},
		{key:'periods', label:'Select at least one period when this course can run', complete: hasDayPeriodRestrictions, ref: periodsStepRef}
	];
	const firstIncompleteRequirement = courseCompletionRequirements.find(requirement => !requirement.complete);

	const num_cols = days.length;
	const day_period_grid = {gridTemplateColumns : `repeat(${num_cols}, 120px)`};
	const specialsCourses = courses.filter(course => course.is_special === '1').sort((courseA, courseB) => {
		return `${courseA.name || ''}`.toLowerCase().localeCompare(`${courseB.name || ''}`.toLowerCase());
	});
	const selectedSpecialCourseIndex = specialsCourses.findIndex(course => `${course.course_id}` === `${selectedCourseID}`);
	const previousSpecialCourse = selectedSpecialCourseIndex > 0 ? specialsCourses[selectedSpecialCourseIndex - 1] : null;
	const nextSpecialCourse = selectedSpecialCourseIndex !== -1 ? specialsCourses[selectedSpecialCourseIndex + 1] : null;
	const isSpecialCourseComplete = (course) => {
		const teachersCanTeachCount = Array.isArray(course.teachers_can_teach) ? course.teachers_can_teach.length : 0;
		const homeroomsCount = Array.isArray(course.course_homerooms) ? course.course_homerooms.length : 0;
		const restricted_day_period_key_set = getCourseRestrictedDayPeriodKeySet(course);
		const restricted_valid_count = [...restricted_day_period_key_set].filter((key) => totalSpecialDayPeriodKeySet.has(key)).length;
		const selectableDayPeriods = Math.max(0, totalSpecialDayPeriodCount - restricted_valid_count);
		return teachersCanTeachCount > 0 && homeroomsCount > 0 && selectableDayPeriods > 0;
	};
	const allSpecialsCoursesComplete = specialsCourses.length > 0 && specialsCourses.every(course => isSpecialCourseComplete(course));
	const moveToNextSpecialCourse = () => {
		if(!nextSpecialCourse) return;
		setSelectedCourseID(nextSpecialCourse.course_id);
	};
	const moveToPreviousSpecialCourse = () => {
		if(!previousSpecialCourse) return;
		setSelectedCourseID(previousSpecialCourse.course_id);
	};
	const getScrollableParent = (element) => {
		if(!element) return null;
		let parent = element.parentElement;
		while(parent) {
			const computed_style = window.getComputedStyle(parent);
			const overflow_y = computed_style.overflowY;
			const can_scroll = (overflow_y === 'auto' || overflow_y === 'scroll') && parent.scrollHeight > parent.clientHeight;
			if(can_scroll && parent !== document.body && parent !== document.documentElement) return parent;
			if(parent === document.body || parent === document.documentElement) return null;
			parent = parent.parentElement;
		}
		return null;
	};
	const scrollToSetupStep = (step_ref) => {
		if(!step_ref?.current) return;
		const target_element = step_ref.current;
		const scroll_parent = getScrollableParent(target_element) || document.querySelector('.main-page-with-sidebar-content');
		if(scroll_parent) {
			const parent_rect = scroll_parent.getBoundingClientRect();
			const target_rect = target_element.getBoundingClientRect();
			const next_top = scroll_parent.scrollTop + (target_rect.top - parent_rect.top) - 96;
			scroll_parent.scrollTo({ top: Math.max(0, next_top), behavior: 'smooth' });
		}
		setShowCourseStatusHelp(false);
	};

	//console.log({courseInfo});

	return (
		<>
		{(subscreenRestricted) ?
		(
			<div className='general-screen-message'>
				<FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/>
				<h1>No Access</h1>
				<div>You do not have access to this screen</div>	
			</div>
		):(subscreenLocked) ?
		(
			<div className='general-screen-message'>
				<FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/>
				<h1>Temporarily Locked</h1>
				<div>This screen is locked until all other prior steps are completed</div>	
			</div>
		):(isLoading || coursesLoading) ?
		(
			<div className='center-text'>
				<img src={require('../../../images/balls.gif')} alt='Loading...' style={{height:'40px'}}/>
			</div>
		):
		(
			<>
			{showEditDataModal &&
				<EditTeacherData schoolInfo={schoolInfo} dataID={currentDataID} toggleEditData={toggleEditData} scheduleInfo={scheduleInfo} periods={periods} teachers={teachers} courses={courses} classrooms={classrooms} students={students} setStudents={setStudents} setDatabaseData={setTeachers} filteredResults={teachers}/>
			}
			<div className='mb-elem-container'>
				<h1 className='progress-main-header'>Specials Courses</h1>
				<p className='progress-subheader elem-manage-homerooms-subheader'>Go through each of your specials and let us know how they should run every cycle.</p>
				<>
						{specialsCourses.length === 0 &&
							<div className='specials-setup-empty-courses'>You have no specials courses to display.</div>
						}
						{!selectedCourseID &&
							<>
								<div className='specials-course-list-container'>
									{specialsCourses.map((course) => {
										const course_id = course.course_id;
										const course_name = course.name;
										const course_code = course.course_code;
										const course_complete = isSpecialCourseComplete(course);

										return (
											<div className='specials-course-list-item' key={course_id}>
												<div className='specials-course-list-name'>{course_name} {course_code && `(${course_code})`}</div>
												<div className={`specials-course-list-status ${course_complete ? 'specials-course-list-status-complete' : ''}`}>
													{course_complete ?
														<>
															<span>Complete</span>
															<FontAwesomeIcon icon={faCheckCircle}/>
														</> :
														<span>Not Complete</span>
													}
												</div>
												<div className={`btn specials-course-list-review-btn ${course_complete ? 'green-btn' : 'dark-blue-btn'}`} onClick={() => setSelectedCourseID(course_id)}>
													{course_complete ? 'Review' : 'Continue'}
												</div>
											</div>
										)
									})}
									<div className='blue-link medium-text specials-setup-add-link' onClick={() => toggleAddNewConnection('courses')}><FontAwesomeIcon icon={faCirclePlus}/> Add Special</div>
									{(openSearches.courses) &&
										<div className='search-add-row-container' style={{marginTop:'8px'}}>
											<div className='search-add-search-container'>
												<div className='search-add-adding-input-container'>
													<input id='search-special-course-input' className='search-add-adding-input' onChange={(e) => fuzzySearch(e, 'courses')} placeholder='Search for course'/>
													<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
												</div>
												<FontAwesomeIcon className='x-cancel search-add-cancel-new-btn' icon={faTimes} onClick={() => toggleAddNewConnection('courses')}/>
											</div>
											{searchResults['courses'].length > 0 &&
												<div className='elem-suggestion-box-parent parent-div'>
													<div className='elem-suggestion-box'>
														{searchResults['courses'].map((course, index) => {
															const course_id = course.course_id;
															const course_name = course.name;
															const course_code = course.course_code;

														return (
															<div className='search-add-search-result' key={index}>
																<div>{course_name} {course_code && `(${course_code})`}</div>
																<SearchAddButton selected={course.is_special === '1'} onAdd={() => updateCourse(course_id, 'is_special', '1')} />
															</div>
														)
													})}
													</div>
												</div>
											}
											{searchResults['courses'].length === 0 &&
												<div className='specials-course-search-empty-message'>Can't find a course? Please check your <Link className='blue-text' to={{pathname:`/app/elementary/school_data/courses/`, redirect:`/app/elementary/progress/auto_scheduling_setup/specials_setup`}}>courses</Link> to make sure the course exists.</div>
											}
										</div>
									}
								</div>
							</>
						}
						{selectedCourseID && courseInfo &&
							<>
							<div className='blue-link medium-text specials-courses-back-link' onClick={() => {sessionStorage.removeItem(selectedCourseStorageKey); setSelectedCourseID(null);}}>
								<FontAwesomeIcon icon={faChevronLeft}/>
								<span>Back to Specials Courses</span>
							</div>
							<div className='specials-setup-specials-container'>
								<div className='specials-setup-question-container'>
									<div>
										{/* <div className='specials-setup-label specials-setup-name'>Special Name</div> */}
										<div className='specials-setup-special-name-row'>
											<div className={`specials-special-name-nav-arrow ${!previousSpecialCourse ? 'specials-special-name-nav-arrow-disabled' : ''}`} onClick={moveToPreviousSpecialCourse}>
												<FontAwesomeIcon icon={faChevronLeft}/>
											</div>
											<div className='specials-setup-special-name specials-setup-special-name-with-nav'>{specialName}</div>
											<div className={`specials-special-name-nav-arrow ${!nextSpecialCourse ? 'specials-special-name-nav-arrow-disabled' : ''}`} onClick={moveToNextSpecialCourse}>
												<FontAwesomeIcon icon={faChevronRight}/>
											</div>
											<div className='specials-setup-course-status-wrapper' ref={courseStatusHelpRef}>
												<div className={`specials-setup-course-status ${selectedCourseComplete ? 'specials-setup-course-status-complete' : 'specials-setup-course-status-pending'}`} onClick={() => setShowCourseStatusHelp(!showCourseStatusHelp)}>
													{selectedCourseComplete ?
														<>
															<FontAwesomeIcon icon={faCheckCircle}/>
															<span>Setup Complete</span>
														</> :
														<>
															<FontAwesomeIcon icon={faTriangleExclamation}/>
															<span>Needs Setup ({selectedCourseCompletedCount}/3)</span>
														</>
													}
													<div className='specials-setup-course-status-help-btn'>?</div>
												</div>
												{selectedCourseComplete && nextSpecialCourse &&
													<div className='specials-setup-next-course-link' onClick={moveToNextSpecialCourse}>
														<span>Move to next course</span>
														<FontAwesomeIcon icon={faChevronRight}/>
													</div>
												}
												{showCourseStatusHelp &&
													<div className='specials-setup-course-status-help-dropdown'>
														<div className='specials-setup-course-status-help-title'>Required to complete this course:</div>
														{courseCompletionRequirements.map((requirement, index) => (
															<div className={`specials-setup-course-status-help-item ${requirement.complete ? 'specials-setup-course-status-help-item-complete' : 'specials-setup-course-status-help-item-pending'}`} key={requirement.key} onClick={() => scrollToSetupStep(requirement.ref)}>
																<div className='specials-setup-course-status-help-number'>{index + 1}</div>
																<div className='specials-setup-course-status-help-text'>{requirement.label}</div>
																<FontAwesomeIcon className={`specials-setup-course-status-help-check ${requirement.complete ? 'specials-setup-course-status-help-check-complete' : 'specials-setup-course-status-help-check-pending'}`} icon={requirement.complete ? faCheckCircle : faTriangleExclamation}/>
															</div>
														))}
													</div>
												}
											</div>
										</div>
									</div>
								</div>
								<div className='specials-setup-question-row'>
								<div className={`specials-setup-question-container ${hasTeachersCanTeach ? 'specials-setup-question-container-complete' : ''} ${firstIncompleteRequirement?.key === 'teachers' ? 'specials-setup-question-container-next' : ''}`} ref={teachersStepRef}>
									<div>
										<div className='specials-setup-label'>Who can teach this course?<span className='specials-setup-required-asterisk'>*</span></div>
										{teachersCanTeach.length === 0 &&
											<div className='specials-setup-display'>No teachers selected yet...</div>
										}
										{teachersCanTeach.map((teacher_can_teach, tct_index) => {
											const teacher_id = teacher_can_teach.teacher_id;
											const teacher_index = teachers.findIndex(teacher => teacher.teacher_id === teacher_id);
											const teacher_info = (teacher_index !== -1) ? teachers[teacher_index] : null;

											if(!teacher_info) return null;

											const teacher_last_name = teacher_info.name;
											const teacher_first_name = teacher_info.first_name;

											return (
												<div className='specials-setup-tct' key={tct_index}>
													<div className='specials-setup-display'>
														<div>{teacher_last_name}, {teacher_first_name}</div>
														<div className='cursor-pointer' onClick={() => updateCourse(selectedCourseID, 'teachers_can_teach', teacher_id, 'remove')}><FontAwesomeIcon icon={faTimes}/></div>
													</div>
													<FontAwesomeIcon className='blue-link' icon={faCalendarDays} onClick={() => toggleEditData(teacher_id)}/>
												</div>
												
											)
										})}
										{(openSearches.teachers) &&
											<div className='search-add-row-container'>
												<div className='search-add-search-container'>
													<div className='search-add-adding-input-container'>
														<input id='search-tct-input' className='search-add-adding-input' onChange={(e) => fuzzySearch(e, 'teachers')} placeholder='Search for teacher'/>
														<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
													</div>
													<FontAwesomeIcon className='x-cancel search-add-cancel-new-btn' icon={faTimes} onClick={() => toggleAddNewConnection('teachers')}/>
												</div>
												{searchResults['teachers'].length > 0 &&
													<div className='elem-suggestion-box-parent parent-div'>
														<div className='elem-suggestion-box'>
															{searchResults['teachers'].map((search_result, index) => {
																const search_teacher_id = search_result.teacher_id;
																const search_teacher_first_name = search_result.first_name;
															const search_teacher_last_name = search_result.name;
															const search_teacher_full_name =  `${search_teacher_last_name}, ${search_teacher_first_name}`;
															const teacher_selected = teachersCanTeach.some(tct_info => tct_info.teacher_id === search_teacher_id);

																return (
																	<div className='search-add-search-result' key={index}>
																		<div>{search_teacher_full_name}</div>
																		<SearchAddButton selected={teacher_selected} onAdd={() => updateCourse(selectedCourseID, 'teachers_can_teach', search_teacher_id, 'add')} />
																	</div>
																)
															})}
														</div>
													</div>
												}
											</div>
										}
										<div className='blue-link medium-text specials-setup-add-link' onClick={() => toggleAddNewConnection('teachers')}><FontAwesomeIcon icon={faCirclePlus}/> Add a Teacher</div>
									</div>
								</div>
								{hasTeachersCanTeach &&
									<FontAwesomeIcon className='specials-setup-question-complete-mark' icon={faCheckCircle}/>
								}
								</div>
								<div className='specials-setup-question-container'>
									<div>
										<div className='specials-setup-label'>Which classrooms does this course use?</div>
										{classroomRestrictions.length === 0 &&
											<div className='specials-setup-display'>No classrooms selected yet...</div>
										}
										{classroomRestrictions.map((classroom_id, cr_index) => {
											const classroom_index = classrooms.findIndex(classroom => classroom.classroom_id === classroom_id);
											const classroom_info = (classroom_index !== -1) ? classrooms[classroom_index] : null;

											if(!classroom_info) return null;

											const classroom_name = classroom_info.classroom_name;

											return (
												<div className='specials-setup-display' key={cr_index}>
													<div>{classroom_name}</div>
													<div className='cursor-pointer' onClick={() => updateCourse(selectedCourseID, 'classroom_restrictions', classroom_id, 'remove')}><FontAwesomeIcon icon={faTimes}/></div>
												</div>
											)
										})}
										{(openSearches.classrooms) &&
											<div className='search-add-row-container'>
												<div className='search-add-search-container'>
													<div className='search-add-adding-input-container'>
														<input id='search-tct-input' className='search-add-adding-input' onChange={(e) => fuzzySearch(e, 'classrooms')} placeholder='Search for classroom'/>
														<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
													</div>
													<FontAwesomeIcon className='x-cancel search-add-cancel-new-btn' icon={faTimes} onClick={() => toggleAddNewConnection('classrooms')}/>
												</div>
												{searchResults['classrooms'].length > 0 &&
													<div className='elem-suggestion-box-parent parent-div'>
														<div className='elem-suggestion-box'>
															{searchResults['classrooms'].map((search_result, index) => {
															const search_classroom_id = search_result.classroom_id;
															const search_classroom_name = search_result.classroom_name;
															const classroom_selected = classroomRestrictions.includes(search_classroom_id);

																return (
																	<div className='search-add-search-result' key={index}>
																		<div>{search_classroom_name}</div>
																		<SearchAddButton selected={classroom_selected} onAdd={() => updateCourse(selectedCourseID, 'classroom_restrictions', search_classroom_id, 'add')} />
																	</div>
																)
															})}
														</div>
													</div>
												}
											</div>
										}
										<div className='blue-link specials-setup-add-link' onClick={() => toggleAddNewConnection('classrooms')}><FontAwesomeIcon icon={faCirclePlus}/> Add a Classroom</div>
									</div>
								</div>
								<div className='specials-setup-question-container'>
									<div>
										<div className='specials-setup-label'>How many times does a homeroom need this per cycle?</div>
										<Dropdown data={numTimesNeededOptions} currentValue={numTimesNeeded} handleChange={(new_value) => updateCourse(selectedCourseID, 'num_times_needed', new_value)} />
									</div>
								</div>
								<div className='specials-setup-question-row'>
								<div className={`specials-setup-question-container ${hasCourseHomerooms ? 'specials-setup-question-container-complete' : ''} ${firstIncompleteRequirement?.key === 'homerooms' ? 'specials-setup-question-container-next' : ''}`} ref={homeroomsStepRef}>
									<div>
										<div className='specials-setup-label'>Which homerooms need this special?<span className='specials-setup-required-asterisk'>*</span></div>
										{courseHomerooms.length === 0 &&
											<div className='specials-setup-display'>No homerooms selected yet...</div>
										}
										{courseHomerooms.map((homeroom_teacher_id, homeroom_index) => {
											const teacher_index = teachers.findIndex(teacher => teacher.teacher_id === homeroom_teacher_id);
											const teacher_info = (teacher_index !== -1) ? teachers[teacher_index] : null;

											if(!teacher_info) return null;

											const teacher_last_name = teacher_info.name;
											const teacher_first_name = teacher_info.first_name;

											return (
												<div className='specials-setup-display' key={homeroom_index}>
													<div>{teacher_last_name}, {teacher_first_name}</div>
													<div className='cursor-pointer' onClick={() => updateCourse(selectedCourseID, 'course_homerooms', homeroom_teacher_id, 'remove')}><FontAwesomeIcon icon={faTimes}/></div>
												</div>
											)
										})}
										{(openSearches.homeroom_teachers) &&
											<div className='search-add-row-container'>
												<div className='search-add-search-container'>
													<div className='search-add-adding-input-container'>
														<input id='search-tct-input' className='search-add-adding-input' onChange={(e) => fuzzySearch(e, 'homeroom_teachers')} placeholder='Search for homeroom teacher'/>
														<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
													</div>
													<FontAwesomeIcon className='x-cancel search-add-cancel-new-btn' icon={faTimes} onClick={() => toggleAddNewConnection('homeroom_teachers')}/>
												</div>
												{searchResults['homeroom_teachers'].length > 0 &&
													<div className='elem-suggestion-box-parent parent-div'>
														<div className='elem-suggestion-box'>
															{searchResults['homeroom_teachers'].map((search_result, index) => {
																const current_result_type = (search_result.result_type === 'grade') ? 'grade' : 'teacher';
																const previous_result = (index > 0) ? searchResults['homeroom_teachers'][index - 1] : null;
																const previous_result_type = (previous_result && previous_result.result_type === 'grade') ? 'grade' : ((previous_result) ? 'teacher' : null);
																const show_type_header = index === 0 || current_result_type !== previous_result_type;
																const type_header_label = (current_result_type === 'grade') ? 'By Grade' : 'By Teacher';

																if(search_result.result_type === 'grade')
																{
																	const grade_display_name = search_result.display_name;
																	const grade_teacher_ids = search_result.teacher_ids;
																	const grade_teacher_count = grade_teacher_ids.length;
																	const grade_selected = grade_teacher_ids.every(teacher_id => courseHomerooms.includes(teacher_id));

																	return (
																		<React.Fragment key={`grade-${search_result.grade_label}-${index}`}>
																			{show_type_header &&
																				<div className='search-add-group-header'>{type_header_label}</div>
																			}
																			<div className='search-add-search-result'>
																				<div><b>{grade_display_name}</b> ({grade_teacher_count} {grade_teacher_count === 1 ? 'teacher' : 'teachers'})</div>
																				<SearchAddButton selected={grade_selected} onAdd={() => addHomeroomTeachersByGrade(selectedCourseID, grade_teacher_ids)} />
																			</div>
																		</React.Fragment>
																	)
																}

																const search_teacher_id = search_result.teacher_id;
																const search_teacher_first_name = search_result.first_name;
																const search_teacher_last_name = search_result.name;
																const search_teacher_full_name =  `${search_teacher_last_name}, ${search_teacher_first_name}`;
																const teacher_selected = courseHomerooms.includes(search_teacher_id);

																return (
																	<React.Fragment key={`teacher-${search_teacher_id}-${index}`}>
																		{show_type_header &&
																			<div className='search-add-group-header'>{type_header_label}</div>
																		}
																		<div className='search-add-search-result'>
																			<div>{search_teacher_full_name}</div>
																			<SearchAddButton selected={teacher_selected} onAdd={() => updateCourse(selectedCourseID, 'course_homerooms', search_teacher_id, 'add')} />
																		</div>
																	</React.Fragment>
																)
															})}
														</div>
													</div>
												}
											</div>
										}
										<div className='blue-link medium-text specials-setup-add-link' onClick={() => toggleAddNewConnection('homeroom_teachers')}><FontAwesomeIcon icon={faCirclePlus}/> Add a Homeroom Teacher</div>
									</div>
								</div>
								{hasCourseHomerooms &&
									<FontAwesomeIcon className='specials-setup-question-complete-mark' icon={faCheckCircle}/>
								}
								</div>
								<div className='specials-setup-question-row'>
								<div className={`specials-setup-question-container ${hasDayPeriodRestrictions ? 'specials-setup-question-container-complete' : ''} ${firstIncompleteRequirement?.key === 'periods' ? 'specials-setup-question-container-next' : ''}`} ref={periodsStepRef}>
									<div>
										<div className='specials-setup-label'>When can this special run?<span className='specials-setup-required-asterisk'>*</span></div>
										<div className='mb-elem-teacher-schedule-mini'>
											<div className='mb-elem-week-schedule-container-mini'>
												<div className='mb-elem-week-schedule-time-container-mini'>
													<div className='mb-elem-time-delimiter-mini'>
														<div>7:00AM</div>
													</div>
													<div className='mb-elem-time-delimiter-mini'>
														<div>8:00AM</div>
													</div>
													<div className='mb-elem-time-delimiter-mini'>
														<div>9:00AM</div>
													</div>
													<div className='mb-elem-time-delimiter-mini'>
														<div>10:00AM</div>
													</div>
													<div className='mb-elem-time-delimiter-mini'>
														<div>11:00AM</div>
													</div>
													<div className='mb-elem-time-delimiter-mini'>
														<div>12:00PM</div>
													</div>
													<div className='mb-elem-time-delimiter-mini'>
														<div>1:00PM</div>
													</div>
													<div className='mb-elem-time-delimiter-mini'>
														<div>2:00PM</div>
													</div>
													<div className='mb-elem-time-delimiter-mini'>
														<div>3:00PM</div>
													</div>
													<div className='mb-elem-time-delimiter-mini'>
														<div>4:00PM</div>
													</div>
												</div>
												<div className='mb-elem-week-schedule-days-container-mini' style={day_period_grid}>
													{days.map(day_number => {
														const day_name = `Day ${day_number}`;

														const day_schedule = periods.filter((period) => {
															if(`${period?.is_special || '0'}` !== '1') return false;
															const period_days = Array.isArray(period?.days) ? period.days.map(day => `${day}`) : [];
															return period_days.includes(`${day_number}`);
														});

														///// Organize all the periods into overlapping groups /////
														// Any non overlapping periods get thrown into their own group
														const day_schedule_with_overlaps = day_schedule.reduce((results, period) => {
															const period_start_time = period.start_time;
															const period_end_time = period.end_time;

															const period_start_time_array = period_start_time.split(':');
															const period_end_time_array = period_end_time.split(':');

															const period_start_time_hour = parseInt(period_start_time_array[0]);
															const period_start_time_min = parseInt(period_start_time_array[1]);
															const period_start_num_hours = period_start_time_hour - 7;
															const period_row_start = Math.max(1, period_start_num_hours*60 + period_start_time_min);

															const period_end_time_hour = parseInt(period_end_time_array[0]);
															const period_end_time_min = parseInt(period_end_time_array[1]);
															const period_end_num_hours = period_end_time_hour - 7;
															const period_row_end = Math.max(1, period_end_num_hours*60 + period_end_time_min);

															period.row_start = period_row_start;
															period.row_end = period_row_end;
															period.period_length = period_row_end - period_row_start;

															results.push(period);
															return results;
														},[]);

														const sorted_day_schedule_with_overlaps = sortArrayOfObjects(day_schedule_with_overlaps, 'period_length', 'number', 'desc');

														const logged_positions = {};

														return (
															<div className='mb-elem-week-day-container-mini' key={`${day_number}`}>
																<h3>{day_name}</h3>
																<div className='mb-elem-week-day-block-parent-container-mini'>
																	<div className='mb-elem-week-day-grid-mini'>
																		{sorted_day_schedule_with_overlaps.map(period => {
																			const period_id = period.period_id;
																			const period_name = period.period_name;
																			
																			const start_time = period.start_time;
																			const end_time = period.end_time;
																			const start_time_formatted = formatTime(start_time);
																			const end_time_date_formatted = formatTime(end_time);

																			const row_start = period.row_start;
																			const row_end = period.row_end;

																			// Calculate the column start and end position on the grid
																			const period_position = calculatePeriodColumnStartEnd(period, day_schedule_with_overlaps, logged_positions, start_time, end_time);
																			
																			const position_index = period_position.position_index;
																			const col_start = period_position.col_start;
																			const col_end = period_position.col_end;

																			logged_positions[period_id] = position_index;

																			const day_is_restricted = selectedCourseRestrictedDayPeriodKeySet.has(getDayPeriodKey(day_number, period_id));
																			
																			return (
																				<div className='mb-elem-block-mini parent-div' style={{gridColumnStart:col_start, gridColumnEnd:col_end, gridRowStart:row_start, gridRowEnd:row_end}} key={period_id}>
																					<div className={`mb-elem-block-inner-mini left-border-green-thick`} onClick={() => toggleDayPeriodSelection(day_number, period_id, day_is_restricted)}>
																						<div>
																							{(!day_is_restricted) ? 
																								(
																									<FontAwesomeIcon
																										className='fas-checkbox-checked'
																										icon={faCheckSquare}
																										onClick={(event) => {
																											event.stopPropagation();
																											toggleDayPeriodSelection(day_number, period_id, day_is_restricted);
																										}}
																									/>
																								) : 
																								(
																									<FontAwesomeIcon
																										className='fas-checkbox-unchecked'
																										icon={faSquare}
																										onClick={(event) => {
																											event.stopPropagation();
																											toggleDayPeriodSelection(day_number, period_id, day_is_restricted);
																										}}
																									/>
																								)
																							}
																						</div>
																						<div className='ellipsis'>
																							<div className={`mb-elem-block-header-mini`}>
																								<span className='tooltip' data-tooltip-text={period_name}>
																									<div className='mb-elem-block-name-mini ellipsis'>{period_name}</div>
																								</span>
																							</div>
																							<div className={`mb-elem-block-content-mini`}>
																								<div className='tooltip' data-tooltip-text={`${start_time_formatted} - ${end_time_date_formatted}`}>
																									<div className='mb-elem-block-time-mini ellipsis'>{start_time_formatted} - {end_time_date_formatted}</div>
																								</div>
																							</div>
																						</div>
																					</div>
																				</div>
																			)
																		})}
																	</div>
																</div>
															</div>
														)
													})}
												</div>
											</div>
										</div>
									</div>
								</div>
								{hasDayPeriodRestrictions &&
									<FontAwesomeIcon className='specials-setup-question-complete-mark' icon={faCheckCircle}/>
								}
								</div>
							</div>
							</>
						}
						{allSpecialsCoursesComplete &&
							<div className='fixed-bottom-save-container'>
								<Button classes={['btn', 'btn-extra-large', 'green-btn', 'align-right', 'move-to-next-screen-btn']} selected={false} text='Go to Next Step: Place Specials' onClickFunction={handleMoveToNextScreen} />
							</div>
						}
				</>
			</div>
		</>
	)
		}
		</>
	)
}
